import { useEffect } from "react";
import { useDispatch, useStore } from "react-redux";
import ChatService from "../../store/services/chatService";
import {
  connectUserSocket,
  closeUserSocket,
} from "../../store/sockets/user/userSocketService";
import {
  receiveUserUpdate,
  syncChatUnreadCounts,
} from "../../store/slices/userUpdates/userUpdatesSlice";
import { getToken, getUserInfo } from "../../utils/session";
import { decodeJwtPayload } from "../../utils/jwt";

export default function useUserUpdates() {
  const dispatch = useDispatch();
  const store = useStore();

  useEffect(() => {
    const userInfo = getUserInfo();
    const username =
      userInfo?.username ||
      userInfo?.user?.username ||
      decodeJwtPayload(getToken()).username ||
      "";
    if (!username) return undefined;

    let active = true;
    let pendingRefresh = null;
    let queuedRefresh = false;
    let socketRevision = 0;
    let legacyMessageCount;
    let hasLiveSnapshot = false;

    const refreshCounts = () => {
      if (!active || pendingRefresh) return;
      const revision = store.getState().userUpdates.unreadRevision;
      const socketVersion = socketRevision;
      pendingRefresh = ChatService.getChats()
        .then((chats) => {
          // A delayed HTTP response must not overwrite a newer socket frame or
          // the chat page's confirmed read/delete state.
          if (
            !active ||
            socketVersion !== socketRevision ||
            revision !== store.getState().userUpdates.unreadRevision
          )
            return;
          const unread = chats.map((chat) =>
            Math.max(0, Number(chat.unread_count ?? chat.unread) || 0),
          );
          dispatch(
            syncChatUnreadCounts({
              messageCount: unread.reduce((sum, count) => sum + count, 0),
              chatCount: unread.filter((count) => count > 0).length,
            }),
          );
        })
        .catch(() => {
          // Retain the last known badge during a temporary network failure.
        })
        .finally(() => {
          pendingRefresh = null;
          if (active && queuedRefresh) {
            queuedRefresh = false;
            refreshCounts();
          }
        });
    };

    refreshCounts();
    connectUserSocket({
      username,
      onOpen: refreshCounts,
      onClose: () => {
        hasLiveSnapshot = false;
      },
      onMessage: (frame) => {
        const isBatch =
          String(frame?.type || "").toLowerCase() === "batch_update";
        if (isBatch && Number.isFinite(frame.unread_chat_count)) {
          hasLiveSnapshot = true;
          socketRevision += 1;
          queuedRefresh = false;
        }
        dispatch(receiveUserUpdate(frame));
        // Older backend deployments only publish a message total. Fetch the
        // actual conversation counts instead of treating ten messages as ten chats.
        if (
          isBatch &&
          !Number.isFinite(frame.unread_chat_count) &&
          Number.isFinite(frame.unread_count) &&
          frame.unread_count !== legacyMessageCount
        ) {
          legacyMessageCount = frame.unread_count;
          socketRevision += 1;
          if (pendingRefresh) queuedRefresh = true;
          else refreshCounts();
        }
      },
    });

    const refreshWhenVisible = () => {
      if (!hasLiveSnapshot && document.visibilityState === "visible")
        refreshCounts();
    };
    window.addEventListener("online", refreshWhenVisible);
    window.addEventListener("focus", refreshWhenVisible);
    document.addEventListener("visibilitychange", refreshWhenVisible);
    return () => {
      active = false;
      window.removeEventListener("online", refreshWhenVisible);
      window.removeEventListener("focus", refreshWhenVisible);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
      closeUserSocket();
    };
  }, [dispatch, store]);
}
